import React, {
    FC,
    createContext,
    useContext,
    ReactNode,
    useEffect,
  } from 'react'
  import { useLocation, useNavigate } from 'react-router-dom'
  import { useAuth } from '../store/auth.store'
  import { useAgent } from '../store/agent.store'
  import URLS from '../enums/route'
  import { ApiInterceptor } from '../services/api.interceptor'
  import { useNotify } from './NotifyProvider'
  
  interface ModalContextType {}
  
  const ModalContext = createContext<ModalContextType | null>(null)
  
  const useAuthProvider = () => {
    const context = useContext(ModalContext)
    if (!context) {
      throw new Error('Can not run without "Auth Context Provider"')
    }
    return context
  }
  
  interface AuthProviderProps {
    children: ReactNode
  }
  
  const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
    const { user, logout } = useAuth() 
    const { agent, impersonating, clearAgent } = useAgent()
    const navigate = useNavigate()
    const { pathname } = useLocation()
    // An agent who has not entered a customer yet has no auth.store.user, but
    // is still logged in and belongs on the customers page.
    const isAuthorized = Boolean(user?.id) || Boolean(agent?.id)
    const { onErrorNotify } = useNotify()
  
    useEffect(() => {
      if (user) {
        checkPermissions()
        const permissionCheckInterval = setInterval(checkPermissions, 60000) // Every 1 min
  
        checkSessionExpiration() // Run session check on mount
        const sessionInterval = setInterval(checkSessionExpiration, 60000) // Every 1 min
  
        return () => {
          clearInterval(permissionCheckInterval)
          clearInterval(sessionInterval)
        }
      }
    }, [user])
  
    useEffect(() => {
      checkSessionExpiration() // Run on every render
    }, [])
  
    useEffect(() => {
      if (
        [
          URLS.AuthHome.LINK,
          URLS.AuthLogin.LINK,
          URLS.AuthStepOne.LINK,
          URLS.AuthStepTwo.LINK,
          URLS.AuthStepThree.LINK,
          URLS.RESET_PASSWORD_STEP_ONE.LINK,
          URLS.RESET_PASSWORD_STEP_TWO.LINK,
          URLS.RESET_PASSWORD_STEP_THREE.LINK,
          URLS.POLICY_PAGE.LINK,
        ].includes(pathname)
      ) {
        if (isAuthorized && pathname !== URLS.POLICY_PAGE.LINK && pathname !== URLS.AREA_PAGE.LINK && pathname !== URLS.AuthLogin.LINK && pathname !== URLS.AuthStepThree.LINK) {
          navigate(URLS.HOME.LINK)
        }
      } else {
        if (!isAuthorized) {
          navigate(URLS.AuthHome.LINK)
        }
      }
    }, [pathname, isAuthorized])

    // The customers page belongs to agents only. A logged-in customer who
    // types the URL is sent home rather than left on a page that would just
    // 403 against /api/agent/customers.
    useEffect(() => {
      if (pathname === URLS.AGENT_CUSTOMERS.LINK && !agent?.id) {
        navigate(isAuthorized ? URLS.HOME.LINK : URLS.AuthHome.LINK)
      }
    }, [pathname, isAuthorized, agent])
  
    /** Logout user */
    const logoutUser = () => {
      logout()
      // Leaves no agent JWT or refresh token behind in localStorage. These
      // machines are shared.
      clearAgent()
      localStorage.removeItem('lastLoginDate')
      navigate(URLS.AuthHome.LINK)
    }
  
    /** Check if user permissions changed in backend */
    const checkPermissions = async () => {
      if (!isAuthorized) return
      // While an agent is inside a customer this poll is wrong twice over: it
      // force-logs-out on a permission mismatch, and AuthController::me()
      // dereferences getPermission() with no null guard, so a customer with no
      // permission row makes it fail outright.
      if (impersonating) return
  
      try {
        const response = await ApiInterceptor<{ permissionsMatch: boolean, success: boolean, isBlocked:boolean  }>('/auth/checkPermission', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ permission: user?.permission, areaPremission: user?.areaPermissions, phone: user?.phone, extId: user?.extId })
        })
        if (!response.permissionsMatch) {
          onErrorNotify('שונו הרשאות המשתמש, נא להתחבר מחדש')
          setTimeout(() => {
            logoutUser()
          }, 3000)
        }
        if(response.isBlocked){
          onErrorNotify(`שים לב, הנך מוגדר כלקוח לא פעיל`)
          setTimeout(() => {
            logoutUser()
          }, 3000)
        }
      } catch (error) {
        console.error('Failed to check permissions', error)
      }
    }
  
    const checkSessionExpiration = () => {
      const lastLogin = localStorage.getItem('lastLoginDate')

      if (!lastLogin) {
        return
      }
  
      const lastLoginTime = new Date(lastLogin).getTime()
      if (isNaN(lastLoginTime)) {
        console.warn('Invalid lastLoginDate format, resetting session')
        localStorage.removeItem('lastLoginDate')
        logoutUser()
        return
      }
  
      const currentTime = Date.now()
      const sessionDuration = 7 * 24 * 60 * 60 * 1000 
      if (currentTime - lastLoginTime >= sessionDuration) {
        onErrorNotify('התחברות פגה תוקף, נא להתחבר מחדש')
        setTimeout(() => {
          logoutUser()
        }, 3000)
      }
    }
  
    useEffect(() => {
      if (isAuthorized) {
        localStorage.setItem('lastLoginDate', new Date().toISOString())
      }
    }, [isAuthorized])
  
    return <ModalContext.Provider value={{}}>{children}</ModalContext.Provider>
  }
  
  export { useAuthProvider, AuthProvider }
  